/* eslint-disable @typescript-eslint/no-inferrable-types */
import {
  Component,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  Input,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import {
  IUiAlertContent,
  TUiAlertTypes,
  ToastService,
  ExtractHttpErrorResponseCodeAndMessage,
  IHttpErrorResponseCodeAndMessage,
  IHttpSingleDataResponse,
} from '@irembo-andela/irembogov3-common';
import {
  IError,
  JsonEditorComponent,
  JsonEditorOptions,
} from '@maaxgr/ang-jsoneditor';
import { IServiceChangeRequest } from '../../../../core/models/service-change-request.model';
import { IServiceTemplate } from '../../../../core/models/service-template/service-template.model';
import { ServiceChangeRequestService } from '../../../../core/services/service-change-request.service';
import { ServiceTemplateService } from '../../../../core/services/service-template.service';
import { Subject, finalize, takeUntil, BehaviorSubject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { ValidationErrors } from '@angular/forms';
import { IFieldError } from '../../../../core/models/service-change-request-config-steps-forms.model';
import { ExtractHttpErrorFieldErrorsUtil } from '../../../../core/utils/http-field-error-extractor.utils';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import {
  DefaultEmptyFormData,
  IFormBuilderFieldsData,
} from '../../../../core/models/form-builder-fields-data.model';
import { EServiceChangeRequestSection } from '../../../../core/models/service-change-request-sections.enum';
import { EServiceChangeRequestToggleHeaders } from '../../../../core/models/service-change-request-toggle-header-section.enum';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { IIremboFormlyFieldOnlyAllowedProps } from '../../../../core/models/irembo-formly-field-allowed-props.type';
import { FormlyFieldArrayToIremboFormlyFieldsConverter } from '../../../../core/utils/irembo-formly-field-allow-pops.utils';

@Component({
  selector: 'irembogov-configuration-form-builder',
  templateUrl: './configuration-form-builder.component.html',
  styleUrls: ['./configuration-form-builder.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ConfigurationFormBuilderComponent implements OnInit, OnDestroy {
  @Input() changeRequest!: IServiceChangeRequest;
  @Input() isTemplateMode = false;
  @Input() template: IServiceTemplate | null = null;
  @Output() jsonFormBuilderFormSubmitted: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  @ViewChild('errorMessageElement') errorMessageElement!: ElementRef;
  @ViewChild(JsonEditorComponent) editor!: JsonEditorComponent;

  DefaultEmptyFormData = DefaultEmptyFormData;

  EServiceChangeRequestSection = EServiceChangeRequestSection;
  EServiceChangeRequestToggleHeaders = EServiceChangeRequestToggleHeaders;

  showConfigFormErrorMessage = false;
  jsonEditorValidationErrors: ValidationErrors[] = [];

  public editorOptions: JsonEditorOptions;
  jsonEditorFormData!: Record<string, IIremboFormlyFieldOnlyAllowedProps[]>;
  isDirtyPreview = false;
  previewLoaded = false;

  formlyFieldsSubjectControl$: Subject<void> = new Subject<void>();
  formChanges$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  autoSaveInProgress = false;
  pendingAutoSaveRequested = false;
  lastAutoSaved: Date | null = null;
  autoSaveTimeout: any = null;
  // The BehaviorSubject replays its current value on subscribe; that first
  // emission is the initial load, not a user edit, so it must not autosave.
  private isInitialFieldsEmission = true;

  formAlertContent: IUiAlertContent = {
    title: '',
    message: '',
    type: 'warning',
  };
  isLoading = false;

  // Owned by the shell; the version toggle now lives in the top header.
  @Input() useV2Builder = false;
  showFullscreenEditor = false;

  constructor(
    private toastService: ToastService,
    private serviceChangeRequestService: ServiceChangeRequestService,
    private serviceTemplateService: ServiceTemplateService,
    private formBuilderService: FormBuilderService
  ) {
    this.editorOptions = new JsonEditorOptions();
    this.editorOptions.modes = ['code'];

    this.editorOptions.onValidate = (json: unknown) =>
      this.checkJsonFormDataIsNotEmpty(json as Record<string, unknown>);
  }

  ngOnInit(): void {
    this.loadJsonEditorFormData({
      fields: this.formBuilderService.formlyFieldsSubject$.getValue().fields,
    });

    this.formBuilderService.formlyFieldsSubject$
      .pipe(takeUntil(this.formlyFieldsSubjectControl$))
      .subscribe((formBuilderFieldsData: IFormBuilderFieldsData) => {
        if (formBuilderFieldsData.forceReload) {
          this.loadJsonEditorFormData({
            fields: [...formBuilderFieldsData.fields],
          });
        }
        // Any emission after the initial load is a real mutation
        // (add/delete/reorder/config/drag) and must trigger autosave —
        // regardless of forceReload. Previously only the forceReload=false
        // branch marked changes, so most edits never autosaved.
        if (this.isInitialFieldsEmission) {
          this.isInitialFieldsEmission = false;
        } else {
          setTimeout(() => this.formChanges$.next(true), 500);
        }
      });

    this.setupAutoSave();
    this.setupRequestedSave();
    this.formBuilderService.reviewModeEnabled$.next(false);

    // Initial autosave check if form already has valid data
    if (this.changeRequest?.id || this.template?.id) {
      setTimeout(() => this.formChanges$.next(true), 1000);
    }
  }

  async validateJsonEditor(): Promise<void> {
    this.isDirtyPreview = true;
    this.previewLoaded = false;
    const editorJson: any = this.editor.getEditor();

    // editorJson.get() throws on syntactically invalid JSON. Catch it so the
    // error surfaces in the UI (error message + disabled Continue) and autosave
    // is blocked — otherwise invalid JSON left the form silently un-gated.
    let jsonData: Record<string, unknown>;
    try {
      jsonData = editorJson.get();
    } catch (e: unknown) {
      const message =
        e instanceof Error ? e.message : 'Invalid JSON syntax in code editor.';
      this.jsonEditorValidationErrors = [
        { line: '', message } as unknown as ValidationErrors,
      ];
      return;
    }

    this.jsonEditorValidationErrors = await editorJson.validate();
    this.formBuilderService.loadJsonFormDataForPreview(jsonData);

    // If valid, trigger autosave
    if (
      this.jsonEditorValidationErrors.length === 0 &&
      jsonData &&
      jsonData['fields'] &&
      Array.isArray(jsonData['fields'])
    ) {
      // Use timeout to ensure data is processed before validation
      setTimeout(() => this.formChanges$.next(true), 100);
    }
  }

  async onFormSubmit(): Promise<void> {
    this.isLoading = true;
    if (this.useV2Builder) {
      this.syncV2BuilderFormDataForSubmit();
    } else {
      await this.validateJsonEditor();
    }

    if (this.isTemplateMode) {
      await this.onTemplateFormSubmit();
    } else {
      await this.onChangeRequestFormSubmit();
    }
  }

  private syncV2BuilderFormDataForSubmit(): void {
    const { fields } = this.formBuilderService.formlyFieldsSubject$.getValue();
    this.jsonEditorValidationErrors = [];
    this.jsonEditorFormData = {
      fields: FormlyFieldArrayToIremboFormlyFieldsConverter(fields) ?? [],
    };
    this.formBuilderService.loadJsonFormDataForPreview(this.jsonEditorFormData);
  }

  async onChangeRequestFormSubmit(): Promise<void> {
    if (!this.changeRequest.id) {
      this.isLoading = false;
      return;
    }
    if (!this.checkIfStepIsValid()) {
      this.isLoading = false;
      return;
    }

    this.updateFormDisplayedErrorMessageContent(false);

    const { fields } = this.formBuilderService.formlyFieldsSubject$.getValue();

    this.serviceChangeRequestService
      .updateServiceChangeRequestFormDefinition(this.changeRequest.id, {
        fields,
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res: IHttpSingleDataResponse<Record<string, unknown>>) => {
          this.toastService.show({
            body: res.responseMessage,
            type: 'success',
          });
          this.lastAutoSaved = new Date();
          this.jsonFormBuilderFormSubmitted.emit(this.checkIfStepIsValid());
        },
        error: (error: HttpErrorResponse) => {
          const errorMessageAndCode: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              `Failed to update Change request form definition`
            );

          this.toastService.show({
            body: errorMessageAndCode.message,
            type: 'error',
          });

          const fieldErrors: IFieldError[] =
            ExtractHttpErrorFieldErrorsUtil(error);

          this.updateFormDisplayedErrorMessageContent(
            true,
            'danger',
            'Form Definition Not Updated',
            errorMessageAndCode.message,
            fieldErrors
          );

          this.jsonFormBuilderFormSubmitted.emit(false);
        },
      });
  }

  async onTemplateFormSubmit(): Promise<void> {
    if (!this.template?.id) {
      this.isLoading = false;
      return;
    }
    if (!this.checkIfStepIsValid()) {
      this.isLoading = false;
      return;
    }

    this.updateFormDisplayedErrorMessageContent(false);

    const { fields } = this.formBuilderService.formlyFieldsSubject$.getValue();

    this.serviceTemplateService
      .updateTemplateFormDefinition(this.template.id, {
        fields,
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (res: IHttpSingleDataResponse<Record<string, unknown>>) => {
          this.toastService.show({
            body: 'Template form definition updated successfully',
            type: 'success',
          });
          this.lastAutoSaved = new Date();
          this.jsonFormBuilderFormSubmitted.emit(this.checkIfStepIsValid());
        },
        error: (error: HttpErrorResponse) => {
          const errorMessageAndCode: IHttpErrorResponseCodeAndMessage =
            ExtractHttpErrorResponseCodeAndMessage(
              error,
              `Failed to update template form definition`
            );

          this.toastService.show({
            body: errorMessageAndCode.message,
            type: 'error',
          });

          const fieldErrors: IFieldError[] =
            ExtractHttpErrorFieldErrorsUtil(error);

          this.updateFormDisplayedErrorMessageContent(
            true,
            'danger',
            'Template Form Definition Not Updated',
            errorMessageAndCode.message,
            fieldErrors
          );

          this.jsonFormBuilderFormSubmitted.emit(false);
        },
      });
  }

  updateFormDisplayedErrorMessageContent(
    show: boolean,
    type: TUiAlertTypes = 'warning',
    title: string = '',
    message: string = '',
    fieldErrors: IFieldError[] | null = null
  ): void {
    let extraMessage = '';
    this.showConfigFormErrorMessage = show;

    if (fieldErrors) {
      extraMessage = fieldErrors
        .map((field: IFieldError) => `${field.field}: ${field?.errorCode}`)
        .join(`<br/>\n`);
    }

    this.formAlertContent = { type, title, message, extraMessage };

    if (show) {
      this.errorMessageElement.nativeElement.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }

  private checkIfStepIsValid(showError: boolean = true): boolean {
    if (
      this.jsonEditorValidationErrors.length > 0 ||
      !this.jsonEditorFormData
    ) {
      if (showError) {
        this.updateFormDisplayedErrorMessageContent(
          true,
          'danger',
          'Invalid JSON Entry!',
          'Please ensure you have filled a valid JSON form entry.'
        );
      }
      return false;
    }

    return true;
  }

  loadJsonEditorFormData(formlyFields: Record<string, FormlyFieldConfig[]>) {
    if (!formlyFields['fields']) {
      this.jsonEditorFormData = { fields: [] };
    }
    this.jsonEditorFormData = {
      fields:
        FormlyFieldArrayToIremboFormlyFieldsConverter(formlyFields['fields']) ??
        [],
    };
  }

  private checkJsonFormDataIsNotEmpty(json: Record<string, unknown>): IError[] {
    const errors: IError[] = [];

    const propsArray: string[] = Object.keys(json);
    if (propsArray.includes('isTrusted')) {
      propsArray.splice(propsArray.indexOf('isTrusted'), 1);
    }

    if (propsArray.length < 1) {
      errors.push({
        path: [1],
        message: 'JSON Object can not be empty.',
      });
    }

    if (propsArray.indexOf('fields') < 0) {
      errors.push({
        path: [1],
        message: 'Missing top level "fields" property.',
      });
    }

    return errors;
  }

  onTabSelectChanged(event: EServiceChangeRequestToggleHeaders) {
    if (event === EServiceChangeRequestToggleHeaders.CODE_EDITOR) {
      this.editor.setMode('code');
    }
    if (event !== EServiceChangeRequestToggleHeaders.WIZARD) {
      this.formBuilderService.reviewModeEnabled$.next(true);
    } else {
      this.formBuilderService.reviewModeEnabled$.next(false);
    }
    this.formBuilderService.refreshFormlyForm();
  }

  private setupAutoSave(): void {
    this.formChanges$
      .pipe(
        debounceTime(3000),
        distinctUntilChanged(),
        takeUntil(this.formlyFieldsSubjectControl$)
      )
      .subscribe(hasChanges => {
        if (
          hasChanges &&
          !this.autoSaveInProgress &&
          (this.changeRequest?.id || this.template?.id)
        ) {
          // Check if fields subject has data to save
          const formData =
            this.formBuilderService.formlyFieldsSubject$.getValue();
          if (formData && formData.fields && formData.fields.length > 0) {
            if (this.jsonEditorValidationErrors.length === 0) {
              this.autoSave();
            }
          }
        }
      });
  }

  private setupRequestedSave(): void {
    this.formBuilderService.formlyFieldsSaveRequested$
      .pipe(takeUntil(this.formlyFieldsSubjectControl$))
      .subscribe(() => {
        if (this.autoSaveInProgress) {
          this.pendingAutoSaveRequested = true;
          return;
        }

        if (this.jsonEditorValidationErrors.length === 0) {
          this.autoSave();
        }
      });
  }

  private autoSave(): void {
    if (!this.changeRequest?.id && !this.template?.id) return;

    this.autoSaveInProgress = true;
    const { fields } = this.formBuilderService.formlyFieldsSubject$.getValue();

    // Ensure we actually have fields to save
    if (!fields || !Array.isArray(fields) || fields.length === 0) {
      this.autoSaveInProgress = false;
      return;
    }

    const updateObservable =
      this.isTemplateMode && this.template?.id
        ? this.serviceTemplateService.updateTemplateFormDefinition(
            this.template.id,
            { fields }
          )
        : this.serviceChangeRequestService.updateServiceChangeRequestFormDefinition(
            this.changeRequest.id!,
            { fields }
          );

    updateObservable
      .pipe(
        finalize(() => {
          this.autoSaveInProgress = false;
          if (
            this.pendingAutoSaveRequested &&
            this.jsonEditorValidationErrors.length === 0
          ) {
            this.pendingAutoSaveRequested = false;
            setTimeout(() => this.autoSave());
          }
        })
      )
      .subscribe({
        next: () => {
          this.lastAutoSaved = new Date();
          // Reset changes flag only on successful save
          this.formChanges$.next(false);
        },
        error: () => {
          // Reset the flag with a timeout to allow for later retries
          if (this.autoSaveTimeout) {
            clearTimeout(this.autoSaveTimeout);
          }

          this.autoSaveTimeout = setTimeout(() => {
            this.formChanges$.next(true);
          }, 10000);
        },
      });
  }

  openFullscreenEditor(): void {
    this.showFullscreenEditor = true;
  }

  closeFullscreenEditor(): void {
    this.showFullscreenEditor = false;
  }

  ngOnDestroy(): void {
    this.formlyFieldsSubjectControl$.next();
    this.formlyFieldsSubjectControl$.complete();
    this.formBuilderService.configureFieldAction$.next(null);

    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
    }
  }
}
